import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import {
  Avatar,
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputBase,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { avatarColor, getAuthorLabel, getAvatarUrl, initials, timeAgo } from "./communityUtils";

const ACCENT = "#0061FE";

function PersonAvatar({ user, size = 32 }) {
  const name = getAuthorLabel({ user });
  return (
    <Avatar
      src={getAvatarUrl({ user }) || undefined}
      alt={name}
      sx={{ width: size, height: size, fontSize: size * 0.38, fontWeight: 700, color: "#fff", bgcolor: avatarColor(name) }}
    >
      {initials(name)}
    </Avatar>
  );
}

PersonAvatar.propTypes = { user: PropTypes.object, size: PropTypes.number };

function CommentItem({ comment, isAuthor, canDelete, onDelete, onReply }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const name = getAuthorLabel(comment);
  return (
    <Stack
      direction="row"
      spacing={1.25}
      sx={{
        opacity: comment.pending ? 0.55 : 1,
        transition: "opacity 200ms ease",
        "&:hover .comment-delete": { opacity: 1 },
      }}
    >
      <PersonAvatar user={comment.user} />
      <Box minWidth={0} flex={1}>
        <Box
          sx={{
            display: "inline-block",
            maxWidth: "100%",
            px: 1.5,
            py: 1,
            borderRadius: "4px 16px 16px 16px",
            bgcolor: dark ? alpha(theme.palette.common.white, 0.08) : "#F1F5F9",
            border: dark ? `1px solid ${alpha(theme.palette.common.white, 0.1)}` : "none",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: 700,
                color: dark ? "#F8FAFC" : "ink.strong",
              }}
              noWrap
            >
              {name}
            </Typography>
            {isAuthor ? (
              <Box
                sx={{
                  px: 0.75,
                  py: 0.1,
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: dark ? "#93C5FD" : ACCENT,
                  bgcolor: dark ? alpha("#60A5FA", 0.18) : "rgba(0,97,254,0.1)",
                }}
              >
                Author
              </Box>
            ) : null}
          </Stack>
          <Typography
            sx={{
              mt: 0.25,
              fontSize: 14,
              lineHeight: 1.5,
              color: dark ? "#E2E8F0" : "ink.main",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {comment.comment}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.4, pl: 1.5, minHeight: 24 }}>
          <Typography sx={{ fontSize: 12, color: dark ? "#94A3B8" : "#94a3b8" }}>
            {comment.pending ? "Sending…" : timeAgo(comment.createdAt || comment.created_at)}
          </Typography>
          {!comment.pending ? (
            <ButtonBase
              onClick={() => onReply(name)}
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: dark ? "#94A3B8" : "#64748b",
                "&:hover": { color: dark ? "#93C5FD" : ACCENT },
              }}
            >
              Reply
            </ButtonBase>
          ) : null}
          {canDelete && !comment.pending ? (
            <Tooltip title="Delete comment">
              <IconButton
                className="comment-delete"
                size="small"
                aria-label="Delete comment"
                onClick={() => onDelete(comment.id)}
                sx={{
                  p: 0.25,
                  color: dark ? "#94A3B8" : "#94a3b8",
                  opacity: { xs: 1, md: 0 },
                  transition: "opacity 140ms ease",
                  "&:hover": { color: "#dc2626" },
                }}
              >
                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );
}

CommentItem.propTypes = {
  comment: PropTypes.object.isRequired,
  isAuthor: PropTypes.bool,
  canDelete: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onReply: PropTypes.func.isRequired,
};

/** Comment list for a question. */
export function CommentList({ comments, loading, authorId, me, onDelete, onReply }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const isAdmin = String(me?.role || "").toLowerCase() === "admin";

  if (loading && !comments.length) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={22} sx={{ color: ACCENT }} />
      </Stack>
    );
  }
  if (!comments.length) {
    return (
      <Stack
        alignItems="center"
        spacing={1}
        py={4}
        sx={{ color: dark ? "#94A3B8" : "#94a3b8", textAlign: "center" }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: dark ? alpha(theme.palette.common.white, 0.08) : "#F1F5F9",
            border: dark ? `1px solid ${alpha(theme.palette.common.white, 0.1)}` : "none",
          }}
        >
          <ForumOutlinedIcon sx={{ fontSize: 24, color: dark ? "#94A3B8" : "#94a3b8" }} />
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: dark ? "#E2E8F0" : "ink.soft" }}>
          No answers yet
        </Typography>
        <Typography sx={{ fontSize: 13, color: "inherit" }}>Be the first to help out.</Typography>
      </Stack>
    );
  }
  return (
    <Stack spacing={1.75}>
      {comments.map((c) => (
        <CommentItem
          key={c.id}
          comment={c}
          isAuthor={Boolean(authorId) && String(c?.user?.id) === String(authorId)}
          canDelete={isAdmin || String(me?.id || "") === String(c?.user?.id || "")}
          onDelete={onDelete}
          onReply={onReply}
        />
      ))}
    </Stack>
  );
}

CommentList.propTypes = {
  comments: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  me: PropTypes.object,
  onDelete: PropTypes.func.isRequired,
  onReply: PropTypes.func.isRequired,
};

/** Pinned comment box: Enter sends, Shift+Enter adds a line. */
export const CommentComposer = forwardRef(function CommentComposer({ me, posting, onSubmit, autoFocus }, ref) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const [text, setText] = useState("");
  const inputRef = useRef(null);
  const canSend = Boolean(text.trim()) && !posting;

  useImperativeHandle(ref, () => ({
    mention(name) {
      setText((t) => `${t && !t.endsWith(" ") ? `${t} ` : t}@${name} `);
      inputRef.current?.focus();
    },
    focus: () => inputRef.current?.focus(),
  }));

  const submit = async () => {
    if (!canSend) return;
    const value = text;
    setText("");
    const ok = await onSubmit(value);
    if (!ok) setText(value);
  };

  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-end">
      <PersonAvatar user={me} size={34} />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "flex-end",
          gap: 0.5,
          pl: 1.75,
          pr: 0.5,
          py: 0.5,
          borderRadius: 3,
          bgcolor: dark ? alpha(theme.palette.common.white, 0.06) : "background.elevated",
          border: dark
            ? `1px solid ${alpha(theme.palette.common.white, 0.12)}`
            : "1px solid rgba(15, 23, 42, 0.1)",
          transition: "border-color 140ms ease, box-shadow 140ms ease, background 140ms ease",
          "&:focus-within": {
            bgcolor: dark ? alpha(theme.palette.common.white, 0.09) : "background.card",
            borderColor: ACCENT,
            boxShadow: dark
              ? "0 0 0 4px rgba(0, 97, 254, 0.22)"
              : "0 0 0 4px rgba(0, 97, 254, 0.12)",
          },
        }}
      >
        <InputBase
          inputRef={inputRef}
          autoFocus={autoFocus}
          multiline
          maxRows={5}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Write an answer… use @Name to mention"
          sx={{
            flex: 1,
            py: 0.75,
            fontSize: 14,
            lineHeight: 1.5,
            color: dark ? "#F1F5F9" : "ink.main",
            "& textarea::placeholder": {
              color: dark ? "#64748B" : undefined,
              opacity: 1,
            },
          }}
        />
        <IconButton
          aria-label="Send"
          onClick={submit}
          disabled={!canSend}
          sx={{
            width: 34,
            height: 34,
            mb: 0.125,
            color: "#fff",
            bgcolor: ACCENT,
            transition: "transform 140ms ease, background 140ms ease",
            "&:hover": { bgcolor: "#0052d6", transform: "scale(1.05)" },
            "&.Mui-disabled": {
              color: dark ? "#94A3B8" : "#fff",
              bgcolor: dark ? alpha(theme.palette.common.white, 0.12) : "#CBD5E1",
            },
          }}
        >
          {posting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ArrowUpwardRoundedIcon sx={{ fontSize: 19 }} />}
        </IconButton>
      </Box>
    </Stack>
  );
});

CommentComposer.propTypes = {
  me: PropTypes.object,
  posting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool,
};
